import { loadExecutions, saveExecutions } from "../local-storage.ts";
import type { ProgressLogEntry, ProjectExecute, ReportingActivityStatus } from "../types/ead-types.ts";
import { canonicalExecutionId, isGhostExecutionId } from "./execution-id.ts";
import {
  isLiveProjectExecute,
  isTerminalProjectRunStatus,
  mergeStoredProjectRunStatus,
  pickPrsRaw,
  ProjectRunStatus,
  readProjectRunStatus,
  syncCanonicalRunFields,
} from "./run-status.ts";
import { hermesClient } from "./hermes-singleton.js";
import { runGatewayPoll } from "./gateway-poll-queue.ts";
import { isHermesNotModified } from "./hermes-client.ts";
import { canPollGateway } from "./poll-coordinator.ts";

const PAGE_SIZE = 25;
const MAX_REMOTE_EXECUTIONS = 80;
const SELECTED_EXECUTION_KEY = "ead-expui.selected-execution.v1";
const ACTIVE_POLL_MS = 10_000;
const LIVE_DETAIL_POLL_MS = 6000;
const INITIALIZING_DETAIL_POLL_MS = 2000;
const IDLE_POLL_MS = 20_000;

const RUNS_TABS = new Set(["runs", "closedRuns", "generateChat", "chat", "chatMinimal"]);

function runOrderValue(ex: ProjectExecute): number {
  const runNo = Number(ex.runNumber || 0);
  if (Number.isFinite(runNo) && runNo > 0) return runNo;
  const created = Number(
    (ex as { createdAt?: number; created_at?: number }).createdAt ??
      (ex as { created_at?: number }).created_at ??
      0,
  );
  if (Number.isFinite(created) && created > 0) return created;
  const started = Number(ex.startTime || 0);
  if (Number.isFinite(started) && started > 0) return started;
  return 0;
}

function sortByRunOrderDesc(a: ProjectExecute, b: ProjectExecute): number {
  const primary = runOrderValue(b) - runOrderValue(a);
  if (primary !== 0) return primary;
  return String(b.id || "").localeCompare(String(a.id || ""));
}

/** Normalize a list/detail execution row from the gateway (snake_case → camelCase). */
export function normalizeExecutionFromApi(raw: ProjectExecute): ProjectExecute {
  return syncCanonicalRunFields(
    applyExecutionApiAliases(raw as unknown as Record<string, unknown>),
  );
}

function includeInExecutionsList(e: ProjectExecute): boolean {
  if (!e.id) return false;
  if (isLiveProjectExecute(e)) return true;
  if (e.startTime != null) return true;
  // Finished runs without startTime (legacy rows) still belong in the sidebar.
  return !isLiveProjectExecute(e);
}

function countProgressLogImages(execution: ProjectExecute): number {
  return (execution.progressLog ?? []).reduce((count, entry) => {
    return count + (entry.imageUrl?.trim() || entry.thumbnailUrl?.trim() ? 1 : 0);
  }, 0);
}

function applyExecutionApiAliases(ex: Record<string, unknown>): ProjectExecute {
  const out = ex as unknown as ProjectExecute;
  const snake = ex.inherited_from_execution_id;
  if (typeof snake === "string" && snake.trim() && !out.inheritedFromExecutionId) {
    out.inheritedFromExecutionId = snake.trim();
  }
  const runSk = ex.run_session_key;
  if (typeof runSk === "string" && runSk.trim() && !out.runSessionKey) {
    out.runSessionKey = runSk.trim();
  }
  const learningMode = ex.learning_mode;
  if (
    typeof learningMode === "string"
    && learningMode.trim()
    && !out.learningMode
  ) {
    out.learningMode = learningMode.trim() as ProjectExecute["learningMode"];
  }
  const pickBool = (v: unknown): boolean | undefined => {
    if (v === true || v === false) return v;
    if (v === 1 || v === 0) return Boolean(v);
    return undefined;
  };
  const pickNum = (v: unknown): number | undefined => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return Number(v);
    return undefined;
  };
  const loginSuccessAt = pickNum(ex.login_success_at_ms);
  if (loginSuccessAt !== undefined && out.loginSuccessAtMs == null) {
    out.loginSuccessAtMs = loginSuccessAt;
  }
  const runningStart = pickNum(ex.running_phase_started_at_ms);
  if (runningStart !== undefined && out.runningPhaseStartedAtMs == null) {
    out.runningPhaseStartedAtMs = runningStart;
  }
  const runningBootstrapDone = pickNum(ex.running_bootstrap_finished_at_ms);
  if (runningBootstrapDone !== undefined && out.runningBootstrapFinishedAtMs == null) {
    out.runningBootstrapFinishedAtMs = runningBootstrapDone;
  }
  const reportDelivered = pickNum(ex.report_delivered_at_ms);
  if (reportDelivered !== undefined && out.reportDeliveredAtMs == null) {
    out.reportDeliveredAtMs = reportDelivered;
  }
  const cycleRev = pickNum(ex.node_report_cycle_revision);
  if (cycleRev !== undefined && out.nodeReportCycleRevision === undefined) {
    out.nodeReportCycleRevision = cycleRev;
  }
  const cycleAttempted = pickNum(ex.node_report_cycle_attempted);
  if (cycleAttempted !== undefined && out.nodeReportCycleAttempted === undefined) {
    out.nodeReportCycleAttempted = cycleAttempted;
  }
  const cycleUpdated = pickNum(ex.node_report_cycle_updated);
  if (cycleUpdated !== undefined && out.nodeReportCycleUpdated === undefined) {
    out.nodeReportCycleUpdated = cycleUpdated;
  }
  const cycleNoNew = pickNum(ex.node_report_cycle_no_new_findings);
  if (cycleNoNew !== undefined && out.nodeReportCycleNoNewFindings === undefined) {
    out.nodeReportCycleNoNewFindings = cycleNoNew;
  }
  const cycleUpdatedAt = pickNum(ex.node_report_cycle_last_updated_at_ms);
  if (cycleUpdatedAt !== undefined && out.nodeReportCycleLastUpdatedAtMs == null) {
    out.nodeReportCycleLastUpdatedAtMs = cycleUpdatedAt;
  }
  const totalAttempted = pickNum(ex.node_report_total_attempted);
  if (totalAttempted !== undefined && out.nodeReportTotalAttempted === undefined) {
    out.nodeReportTotalAttempted = totalAttempted;
  }
  const totalUpdated = pickNum(ex.node_report_total_updated);
  if (totalUpdated !== undefined && out.nodeReportTotalUpdated === undefined) {
    out.nodeReportTotalUpdated = totalUpdated;
  }
  const totalNoNew = pickNum(ex.node_report_total_no_new_findings);
  if (totalNoNew !== undefined && out.nodeReportTotalNoNewFindings === undefined) {
    out.nodeReportTotalNoNewFindings = totalNoNew;
  }
  if (out.abandoned === undefined && ex.abandoned !== undefined) {
    const b = pickBool(ex.abandoned);
    if (b !== undefined) out.abandoned = b;
  }
  if (!out.abandonedReason && typeof ex.abandoned_reason === "string") {
    out.abandonedReason = ex.abandoned_reason.trim() || null;
  }
  const abandonedAt = pickNum(ex.abandoned_at_ms);
  if (abandonedAt !== undefined && out.abandonedAtMs == null) {
    out.abandonedAtMs = abandonedAt;
  }
  if (out.hasDeliveredPfmMapReport === undefined && ex.has_delivered_pfm_map_report !== undefined) {
    const b = pickBool(ex.has_delivered_pfm_map_report);
    if (b !== undefined) out.hasDeliveredPfmMapReport = b;
  }
  if (out.pfmHasCommittedSnapshot === undefined && ex.pfm_has_committed_snapshot !== undefined) {
    const b = pickBool(ex.pfm_has_committed_snapshot);
    if (b !== undefined) out.pfmHasCommittedSnapshot = b;
  }
  const displayMode = ex.pfm_map_display_mode ?? ex.pfmMapDisplayMode;
  if (displayMode === "current" || displayMode === "previous") {
    out.pfmMapDisplayMode = displayMode;
  }
  const pickNumEither = (snake: unknown, camel: unknown): number | undefined =>
    pickNum(snake) ?? pickNum(camel);
  const sourceRun = pickNumEither(ex.pfm_source_run_number, ex.pfmSourceRunNumber);
  if (sourceRun !== undefined) out.pfmSourceRunNumber = sourceRun;
  const currentRun = pickNumEither(ex.current_run_number, ex.currentRunNumber);
  if (currentRun !== undefined) out.currentRunNumber = currentRun;
  const currentPfmRev = pickNumEither(ex.current_pfm_revision, ex.currentPfmRevision);
  if (currentPfmRev !== undefined) out.currentPfmRevision = currentPfmRev;
  const prevPfmRun = pickNumEither(ex.previous_pfm_run_number, ex.previousPfmRunNumber);
  if (prevPfmRun !== undefined) out.previousPfmRunNumber = prevPfmRun;
  const prevPfmRev = pickNumEither(ex.previous_pfm_revision, ex.previousPfmRevision);
  if (prevPfmRev !== undefined) out.previousPfmRevision = prevPfmRev;
  const bl = ex.pfm_baseline_execution_id;
  if (typeof bl === "string" && bl.trim() && !out.pfmBaselineExecutionId) {
    out.pfmBaselineExecutionId = bl.trim();
  }
  const treeRead = ex.pfm_tree_read_execution_id;
  if (typeof treeRead === "string" && treeRead.trim() && !out.pfmTreeReadExecutionId) {
    out.pfmTreeReadExecutionId = treeRead.trim();
  }
  if (out.startTime == null) {
    const st = pickNum(ex.start_time) ?? pickNum(ex.createdAt) ?? pickNum(ex.created_at);
    if (st !== undefined) out.startTime = st;
  }
  if (out.pfmBaselineTreeVersion === undefined) {
    const n = pickNumEither(ex.pfm_baseline_tree_version, ex.pfmBaselineTreeVersion);
    if (n !== undefined) out.pfmBaselineTreeVersion = n;
  }
  if (out.runNumber === undefined) {
    const n = pickNumEither(ex.run_number, ex.runNumber);
    if (n !== undefined) out.runNumber = n;
  }
  const pfmRev = pickNumEither(ex.pfm_revision, ex.pfmRevision);
  if (pfmRev !== undefined) out.pfmRevision = pfmRev;
  if (out.pfmGenerationVersion === undefined) {
    const n = pickNumEither(ex.pfm_generation_version, ex.pfmGenerationVersion);
    if (n !== undefined) out.pfmGenerationVersion = n;
  }
  if (out.pfmGenerationVersion === undefined && out.runNumber !== undefined) {
    out.pfmGenerationVersion = out.runNumber;
  }
  if (out.pfmRevision === undefined) {
    const n = pickNumEither(ex.pfm_revision, ex.pfmRevision);
    if (n !== undefined) out.pfmRevision = n;
  }
  if (out.pfmLineageVersion === undefined) {
    const n = pickNumEither(ex.pfm_lineage_version, ex.pfmLineageVersion);
    if (n !== undefined) out.pfmLineageVersion = n;
  }
  if (out.pfmStepVersion === undefined) {
    const n = pickNumEither(ex.pfm_step_version, ex.pfmStepVersion);
    if (n !== undefined) out.pfmStepVersion = n;
  }
  // Camel-case aliases from the same server payload (not client-side version math).
  if (out.pfmGenerationVersion === undefined && out.pfmLineageVersion !== undefined) {
    out.pfmGenerationVersion = out.pfmLineageVersion;
  }
  if (out.pfmRevision === undefined && out.pfmStepVersion !== undefined) {
    out.pfmRevision = out.pfmStepVersion;
  }
  if (out.pfmFinalized === undefined && ex.pfm_finalized !== undefined) {
    const b = pickBool(ex.pfm_finalized);
    if (b !== undefined) out.pfmFinalized = b;
  }
  const phase = ex.pfm_snapshot_phase;
  if (typeof phase === "string" && phase.trim() && !out.pfmSnapshotPhase) {
    out.pfmSnapshotPhase = phase.trim();
  }
  const ces = ex.pfm_canonical_evaluation_status;
  if (typeof ces === "string" && ces.trim() && !out.pfmCanonicalEvaluationStatus) {
    out.pfmCanonicalEvaluationStatus = ces.trim();
  }
  if (out.pfmCanonicalReplaceRecommended === undefined && ex.pfm_canonical_replace_recommended !== undefined) {
    const b = pickBool(ex.pfm_canonical_replace_recommended);
    if (b !== undefined) out.pfmCanonicalReplaceRecommended = b;
  }
  const cconf = ex.pfm_canonical_evaluation_confidence;
  if (typeof cconf === "number" && out.pfmCanonicalEvaluationConfidence === undefined) {
    out.pfmCanonicalEvaluationConfidence = cconf;
  }
  const crat = ex.pfm_canonical_evaluation_rationale;
  if (typeof crat === "string" && !out.pfmCanonicalEvaluationRationale) {
    out.pfmCanonicalEvaluationRationale = crat;
  }
  const cat = ex.pfm_canonical_evaluation_at_ms;
  if (typeof cat === "number" && out.pfmCanonicalEvaluationAtMs === undefined) {
    out.pfmCanonicalEvaluationAtMs = cat;
  }
  if (out.pfmCanonicalPromotionApplied === undefined && ex.pfm_canonical_promotion_applied !== undefined) {
    const b = pickBool(ex.pfm_canonical_promotion_applied);
    if (b !== undefined) out.pfmCanonicalPromotionApplied = b;
  }
  if (!out.reportingActivityStatus && typeof ex.reporting_activity_status === "string") {
    const v = String(ex.reporting_activity_status).trim().toLowerCase();
    if (v === "active" || v === "closed") {
      out.reportingActivityStatus = v as ReportingActivityStatus;
    }
  }
  if (out.chatDeliveryActive === undefined && ex.chat_delivery_active !== undefined) {
    const b = pickBool(ex.chat_delivery_active);
    if (b !== undefined) out.chatDeliveryActive = b;
  }
  if (out.chatDeliveryActive === undefined && out.reportingActivityStatus) {
    out.chatDeliveryActive = out.reportingActivityStatus === "active";
  }
  const plc = pickNum(ex.progress_log_count);
  if (plc !== undefined) out.progressLogCount = plc;
  const rc = pickNum(ex.reports_count);
  if (rc !== undefined) out.reportsCount = rc;
  if (out.bootstrapPending === undefined && ex.bootstrap_pending !== undefined) {
    const b = pickBool(ex.bootstrap_pending);
    if (b !== undefined) out.bootstrapPending = b;
  }
  const prs = pickNum(ex.projectRunStatus) ?? pickNum(ex.project_run_status);
  if (prs !== undefined && prs >= 0 && prs <= 7) {
    out.projectRunStatus = prs as ProjectExecute["projectRunStatus"];
  }
  if (!out.progressLog && Array.isArray(ex.progress_log)) {
    out.progressLog = (ex.progress_log as unknown[]).map(normalizeProgressLogEntry);
  } else if (Array.isArray(out.progressLog)) {
    out.progressLog = out.progressLog.map((entry) => normalizeProgressLogEntry(entry));
  }
  const aiLang = ex.aiAnswerLanguage ?? ex.ai_answer_language;
  if (typeof aiLang === "string" && aiLang.trim() && !out.aiAnswerLanguage) {
    out.aiAnswerLanguage = aiLang.trim();
  }
  return syncCanonicalRunFields(out);
}

export function unwrapExecutionFromRunResponse(raw: unknown): ProjectExecute | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const boxed = [o, o.execution, o.run, o.data, o.execution_record].find(
    (v) =>
      v &&
      typeof v === "object" &&
      typeof (v as ProjectExecute).id === "string" &&
      typeof (v as ProjectExecute).status === "string",
  );
  if (boxed && typeof boxed === "object") {
    return applyExecutionApiAliases(boxed as Record<string, unknown>);
  }
  return null;
}

function unwrapExecutionFromGet(raw: unknown): ProjectExecute | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const boxed = [o.execution, o.run, o.data].find(
    (v) =>
      v &&
      typeof v === "object" &&
      typeof (v as ProjectExecute).id === "string" &&
      typeof (v as ProjectExecute).status === "string",
  );
  if (boxed) return applyExecutionApiAliases(boxed as Record<string, unknown>);
  if (typeof o.id === "string" && typeof o.status === "string") {
    return applyExecutionApiAliases(o);
  }
  return null;
}

/** Parse POST /executions/run (and similar) bodies where id may be nested or snake_cased. */
export function unwrapExecutionIdFromRunResponse(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const nested = [o.execution, o.run, o.data, o.execution_record];
  for (const box of nested) {
    if (box && typeof box === "object") {
      const b = box as Record<string, unknown>;
      if (typeof b.id === "string" && b.id.trim()) return b.id.trim();
    }
  }
  if (typeof o.id === "string" && o.id.trim()) return o.id.trim();
  if (typeof o.execution_id === "string" && o.execution_id.trim()) return o.execution_id.trim();
  if (typeof o.executionId === "string" && o.executionId.trim()) return o.executionId.trim();
  return null;
}

function pickInheritedFromExecutionIdFromRecord(rec: Record<string, unknown>): string | null {
  const direct =
    rec.inheritedFromExecutionId ??
    rec.inherited_from_execution_id ??
    rec.pfm_inherited_from_execution_id;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  return null;
}

/**
 * Extract execution id and optional PFM inheritance source from a run creation response.
 */
export function unwrapPfmInheritanceMetadataFromRunResponse(raw: unknown): {
  executionId: string | null;
  inheritedFromExecutionId: string | null;
} {
  const executionId = unwrapExecutionIdFromRunResponse(raw);
  if (!raw || typeof raw !== "object") {
    return { executionId, inheritedFromExecutionId: null };
  }
  const o = raw as Record<string, unknown>;
  const boxes = [o, o.execution, o.run, o.data, o.execution_record].filter(
    (x): x is Record<string, unknown> => Boolean(x && typeof x === "object"),
  );
  let inheritedFromExecutionId: string | null = null;
  for (const b of boxes) {
    inheritedFromExecutionId = pickInheritedFromExecutionIdFromRecord(b);
    if (inheritedFromExecutionId) break;
  }
  return { executionId, inheritedFromExecutionId };
}

function normalizeProgressLogEntry(raw: unknown): ProgressLogEntry {
  if (!raw || typeof raw !== "object") {
    return { ts: 0, kind: "system", text: "" };
  }
  const row = raw as Record<string, unknown>;
  const toolInputRaw = row.toolInput ?? row.tool_input;
  let toolInput: Record<string, unknown> | undefined;
  if (toolInputRaw && typeof toolInputRaw === "object") {
    const ti = toolInputRaw as Record<string, unknown>;
    const thumbs = ti.thumbnailUrls ?? ti.thumbnail_urls;
    toolInput = { ...ti };
    if (Array.isArray(thumbs)) {
      toolInput.thumbnailUrls = thumbs;
      toolInput.thumbnail_urls = thumbs;
    }
  }
  return {
    ts: Number(row.ts || 0),
    kind: String(row.kind || "system"),
    text: String(row.text || ""),
    imageUrl: String(row.imageUrl || row.image_url || "").trim() || undefined,
    thumbnailUrl: String(row.thumbnailUrl || row.thumbnail_url || "").trim() || undefined,
    toolName: String(row.toolName || row.tool_name || "").trim() || undefined,
    toolInput,
  };
}

function progressLogStableKey(entry: ProgressLogEntry): string {
  const tsRaw = Number(entry.ts || 0);
  const kind = String(entry.kind || "");
  const tool = String(entry.toolName || "");
  const ti = (entry.toolInput || {}) as Record<string, unknown>;
  const reminderType = String(ti.reminder_type || ti.reminderType || "").trim();
  const textHead = String(entry.text || ti.title || "").slice(0, 160);
  if (reminderType || tool.includes("_reminder")) {
    const tsKey = Number.isFinite(tsRaw) ? tsRaw.toFixed(3) : "0";
    return `${tsKey}|${kind}|${tool}|${reminderType}|${textHead}`;
  }
  const tsBucket = Math.floor(tsRaw);
  const title = String(ti.title || entry.text || "").slice(0, 96);
  return `${tsBucket}|${kind}|${tool}|${title}`;
}

function mergeProgressLogEntry(
  base: ProgressLogEntry,
  fresh: ProgressLogEntry,
): ProgressLogEntry {
  const merged: ProgressLogEntry = { ...base, ...fresh };
  if (!String(fresh.imageUrl || "").trim() && String(base.imageUrl || "").trim()) {
    merged.imageUrl = base.imageUrl;
  }
  if (!String(fresh.thumbnailUrl || "").trim() && String(base.thumbnailUrl || "").trim()) {
    merged.thumbnailUrl = base.thumbnailUrl;
  }
  const baseTi = (base.toolInput || {}) as Record<string, unknown>;
  const freshTi = (fresh.toolInput || {}) as Record<string, unknown>;
  const baseThumbs = (baseTi.thumbnailUrls ?? baseTi.thumbnail_urls) as unknown;
  const freshThumbs = (freshTi.thumbnailUrls ?? freshTi.thumbnail_urls) as unknown;
  if (
    (!Array.isArray(freshThumbs) || !freshThumbs.length) &&
    Array.isArray(baseThumbs) &&
    baseThumbs.length
  ) {
    merged.toolInput = { ...freshTi, thumbnailUrls: baseThumbs, thumbnail_urls: baseThumbs };
  }
  return merged;
}

function progressLogEntryKey(entry: ProgressLogEntry): string {
  return progressLogStableKey(entry);
}

/** Union progress log rows so list polls cannot wipe a longer cached tail. */
function mergeProgressLogs(
  base: ProgressLogEntry[] | undefined,
  fresh: ProgressLogEntry[] | undefined,
  freshSeq: number | undefined,
  baseSeq: number | undefined,
): ProgressLogEntry[] | undefined {
  if (fresh === undefined) return base;
  if (!base?.length) return fresh;
  if (!fresh.length) {
    return (freshSeq ?? 0) >= (baseSeq ?? 0) ? fresh : base;
  }
  const byKey = new Map<string, ProgressLogEntry>();
  for (const entry of base) {
    const normalized = normalizeProgressLogEntry(entry);
    const key = progressLogEntryKey(normalized);
    byKey.set(key, normalized);
  }
  for (const entry of fresh) {
    const normalized = normalizeProgressLogEntry(entry);
    const key = progressLogEntryKey(normalized);
    const existing = byKey.get(key);
    byKey.set(key, existing ? mergeProgressLogEntry(existing, normalized) : normalized);
  }
  return [...byKey.values()].sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
}

export function mergeExecution(base: ProjectExecute, fresh: ProjectExecute): ProjectExecute {
  const merged: ProjectExecute = { ...base, ...fresh };
  merged.progressLog = mergeProgressLogs(
    base.progressLog,
    fresh.progressLog,
    fresh.progressLogSeq,
    base.progressLogSeq,
  );
  // Detail refresh must not wipe gateway-computed PFM pointers when absent on the payload.
  if (fresh.pfmHasCommittedSnapshot === undefined && base.pfmHasCommittedSnapshot !== undefined) {
    merged.pfmHasCommittedSnapshot = base.pfmHasCommittedSnapshot;
  }
  // Live runs: never drop a committed snapshot on partial list polls (explicit false only).
  if (
    base.pfmHasCommittedSnapshot === true
    && fresh.pfmHasCommittedSnapshot !== true
    && fresh.pfmHasCommittedSnapshot !== false
    && !isTerminalProjectRunStatus(readProjectRunStatus(merged))
  ) {
    merged.pfmHasCommittedSnapshot = true;
  }
  if (base.pfmHasCommittedSnapshot === true && fresh.pfmHasCommittedSnapshot !== false) {
    if (
      !fresh.pfmMapDisplayMode
      && base.pfmMapDisplayMode === "current"
      && base.hasDeliveredPfmMapReport === true
    ) {
      merged.pfmMapDisplayMode = "current";
    }
    const ownId = String(merged.id || base.id || "").trim();
    if (
      ownId
      && !(merged.pfmTreeReadExecutionId || "").trim()
      && base.hasDeliveredPfmMapReport === true
    ) {
      merged.pfmTreeReadExecutionId = ownId;
    }
  }
  if (!(fresh.pfmBaselineExecutionId || "").trim() && (base.pfmBaselineExecutionId || "").trim()) {
    merged.pfmBaselineExecutionId = base.pfmBaselineExecutionId;
  }
  if (!(fresh.pfmTreeReadExecutionId || "").trim() && !(merged.pfmTreeReadExecutionId || "").trim() && (base.pfmTreeReadExecutionId || "").trim()) {
    merged.pfmTreeReadExecutionId = base.pfmTreeReadExecutionId;
  }
  if (!fresh.pfmSnapshotPhase && base.pfmSnapshotPhase) {
    merged.pfmSnapshotPhase = base.pfmSnapshotPhase;
  }
  if (fresh.pfmGenerationVersion !== undefined) {
    merged.pfmGenerationVersion = fresh.pfmGenerationVersion;
    merged.pfmLineageVersion = fresh.pfmLineageVersion ?? fresh.pfmGenerationVersion;
  }
  if (fresh.pfmRevision !== undefined) {
    merged.pfmRevision = fresh.pfmRevision;
    merged.pfmStepVersion = fresh.pfmStepVersion ?? fresh.pfmRevision;
  }
  if (fresh.pfmFinalized === undefined && base.pfmFinalized !== undefined) {
    merged.pfmFinalized = base.pfmFinalized;
  }
  if ((fresh.pfmBaselineTreeVersion ?? 0) <= 0 && (base.pfmBaselineTreeVersion ?? 0) > 0) {
    merged.pfmBaselineTreeVersion = base.pfmBaselineTreeVersion;
  }
  if ((fresh.previousPfmRunNumber ?? 0) <= 0 && (base.previousPfmRunNumber ?? 0) > 0) {
    merged.previousPfmRunNumber = base.previousPfmRunNumber;
  }
  if ((fresh.previousPfmRevision ?? 0) <= 0 && (base.previousPfmRevision ?? 0) > 0) {
    merged.previousPfmRevision = base.previousPfmRevision;
  }
  if ((fresh.currentRunNumber ?? 0) <= 0 && (base.currentRunNumber ?? 0) > 0) {
    merged.currentRunNumber = base.currentRunNumber;
  }
  if ((fresh.currentPfmRevision ?? 0) <= 0 && (base.currentPfmRevision ?? 0) > 0) {
    merged.currentPfmRevision = base.currentPfmRevision;
  }
  if ((fresh.pfmSourceRunNumber ?? 0) <= 0 && (base.pfmSourceRunNumber ?? 0) > 0) {
    merged.pfmSourceRunNumber = base.pfmSourceRunNumber;
  }
  if (fresh.chatDeliveryActive === undefined && base.chatDeliveryActive !== undefined) {
    merged.chatDeliveryActive = base.chatDeliveryActive;
  }
  if (!(fresh.runSessionKey || "").trim() && (base.runSessionKey || "").trim()) {
    merged.runSessionKey = base.runSessionKey;
  }
  const ownId = String(merged.id || base.id || "").trim();
  const freshTreeRead = String(fresh.pfmTreeReadExecutionId || "").trim();
  if (fresh.pfmMapDisplayMode === "current" || fresh.pfmMapDisplayMode === "previous") {
    if (
      fresh.pfmMapDisplayMode === "current"
      && freshTreeRead
      && ownId
      && freshTreeRead !== ownId
    ) {
      merged.pfmMapDisplayMode = "previous";
      merged.pfmTreeReadExecutionId = freshTreeRead;
    } else {
      merged.pfmMapDisplayMode = fresh.pfmMapDisplayMode;
    }
    if (fresh.pfmHasCommittedSnapshot !== undefined) {
      merged.pfmHasCommittedSnapshot = fresh.pfmHasCommittedSnapshot === true;
    }
    if (fresh.hasDeliveredPfmMapReport !== undefined) {
      merged.hasDeliveredPfmMapReport = fresh.hasDeliveredPfmMapReport === true;
    }
    if (freshTreeRead) {
      merged.pfmTreeReadExecutionId = freshTreeRead;
    }
    if ((fresh.pfmBaselineExecutionId || "").trim()) {
      merged.pfmBaselineExecutionId = fresh.pfmBaselineExecutionId;
    }
    if (fresh.pfmSourceRunNumber !== undefined) {
      merged.pfmSourceRunNumber = fresh.pfmSourceRunNumber;
    }
  } else if (base.pfmMapDisplayMode === "current" || base.pfmMapDisplayMode === "previous") {
    merged.pfmMapDisplayMode = base.pfmMapDisplayMode;
    if (base.pfmHasCommittedSnapshot !== undefined && fresh.pfmHasCommittedSnapshot === undefined) {
      merged.pfmHasCommittedSnapshot = base.pfmHasCommittedSnapshot;
    }
    if (!(merged.pfmTreeReadExecutionId || "").trim() && (base.pfmTreeReadExecutionId || "").trim()) {
      merged.pfmTreeReadExecutionId = base.pfmTreeReadExecutionId;
    }
    if (!(merged.pfmBaselineExecutionId || "").trim() && (base.pfmBaselineExecutionId || "").trim()) {
      merged.pfmBaselineExecutionId = base.pfmBaselineExecutionId;
    }
    if ((fresh.pfmSourceRunNumber ?? 0) <= 0 && (base.pfmSourceRunNumber ?? 0) > 0) {
      merged.pfmSourceRunNumber = base.pfmSourceRunNumber;
    }
  } else if (base.pfmHasCommittedSnapshot === true) {
    merged.pfmHasCommittedSnapshot = true;
    if (base.pfmMapDisplayMode === "current" || base.pfmMapDisplayMode === "previous") {
      merged.pfmMapDisplayMode = base.pfmMapDisplayMode;
    } else if (!merged.pfmMapDisplayMode) {
      merged.pfmMapDisplayMode = base.hasDeliveredPfmMapReport === true ? "current" : "previous";
    }
    if (ownId && base.hasDeliveredPfmMapReport === true && !(merged.pfmTreeReadExecutionId || "").trim()) {
      merged.pfmTreeReadExecutionId = ownId;
    } else if (!(merged.pfmTreeReadExecutionId || "").trim() && (base.pfmTreeReadExecutionId || "").trim()) {
      merged.pfmTreeReadExecutionId = base.pfmTreeReadExecutionId;
    }
  } else {
    const basePrevious =
      base.pfmMapDisplayMode === "previous" &&
      base.pfmHasCommittedSnapshot !== true &&
      Boolean((base.pfmTreeReadExecutionId || base.pfmBaselineExecutionId || "").trim());
    const freshOwnTreeCommitted =
      fresh.pfmHasCommittedSnapshot === true
      && String(fresh.pfmTreeReadExecutionId || fresh.id || "").trim() === String(fresh.id || base.id || "").trim();
    const freshCurrent =
      fresh.pfmMapDisplayMode === "current" && freshOwnTreeCommitted;
    if (basePrevious && !freshCurrent) {
      merged.pfmMapDisplayMode = "previous";
      merged.pfmHasCommittedSnapshot = false;
      merged.hasDeliveredPfmMapReport = false;
      merged.pfmTreeReadExecutionId = base.pfmTreeReadExecutionId || base.pfmBaselineExecutionId;
      merged.pfmBaselineExecutionId = base.pfmBaselineExecutionId || base.pfmTreeReadExecutionId;
      if ((base.pfmSourceRunNumber ?? 0) > 0) {
        merged.pfmSourceRunNumber = base.pfmSourceRunNumber;
      }
      if ((base.pfmRevision ?? 0) > 0 && (fresh.pfmRevision ?? 0) <= 0) {
        merged.pfmRevision = base.pfmRevision;
        merged.pfmStepVersion = base.pfmStepVersion ?? base.pfmRevision;
      }
      if ((base.pfmBaselineTreeVersion ?? 0) > 0) {
        merged.pfmBaselineTreeVersion = base.pfmBaselineTreeVersion;
      }
    }
  }

  const mergedPrsStored = mergeStoredProjectRunStatus(base, fresh);
  if (mergedPrsStored !== undefined) {
    merged.projectRunStatus = mergedPrsStored as ProjectExecute["projectRunStatus"];
  }

  const mergedPrs = readProjectRunStatus(merged);

  if (isTerminalProjectRunStatus(mergedPrs)) {
    syncCanonicalRunFields(merged);
    return merged;
  }

  const runStartMs = Number(fresh.startTime || base.startTime || 0);
  const pickRunningMs = (v: unknown): number | undefined => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const freshRunningMs =
    pickRunningMs(fresh.runningPhaseStartedAtMs)
    ?? pickRunningMs((fresh as { running_phase_started_at_ms?: number }).running_phase_started_at_ms);
  const baseRunningMs =
    pickRunningMs(base.runningPhaseStartedAtMs)
    ?? pickRunningMs((base as { running_phase_started_at_ms?: number }).running_phase_started_at_ms);
  const freshLoginSuccessMs =
    pickRunningMs(fresh.loginSuccessAtMs)
    ?? pickRunningMs((fresh as { login_success_at_ms?: number }).login_success_at_ms);
  const baseLoginSuccessMs =
    pickRunningMs(base.loginSuccessAtMs)
    ?? pickRunningMs((base as { login_success_at_ms?: number }).login_success_at_ms);
  const freshBootstrapMs =
    pickRunningMs(fresh.runningBootstrapFinishedAtMs)
    ?? pickRunningMs((fresh as { running_bootstrap_finished_at_ms?: number }).running_bootstrap_finished_at_ms);
  const baseBootstrapMs =
    pickRunningMs(base.runningBootstrapFinishedAtMs)
    ?? pickRunningMs((base as { running_bootstrap_finished_at_ms?: number }).running_bootstrap_finished_at_ms);

  merged.durationMs = mergedPrs === ProjectRunStatus.Initializing ? 0 : merged.durationMs;
  merged.timeBudgetExceeded = false;
  merged.loginSuccessAtMs = freshLoginSuccessMs ?? baseLoginSuccessMs;
  merged.runningPhaseStartedAtMs = freshRunningMs ?? baseRunningMs;
  merged.runningBootstrapFinishedAtMs = freshBootstrapMs ?? baseBootstrapMs;
  if (fresh.operatorStopKind === undefined) {
    merged.operatorStopKind = undefined;
  }

  if (mergedPrs === ProjectRunStatus.Running) {
    const anchorMs = merged.runningPhaseStartedAtMs ?? merged.loginSuccessAtMs;
    if (anchorMs != null && runStartMs > 0 && anchorMs < runStartMs - 60_000) {
      merged.runningPhaseStartedAtMs = freshRunningMs ?? baseRunningMs ?? undefined;
      merged.loginSuccessAtMs = freshLoginSuccessMs ?? baseLoginSuccessMs ?? undefined;
    }
  }

  syncCanonicalRunFields(merged);
  return merged;
}

function executionsSnapshot(executions: ProjectExecute[], selectedId: string | null): string {
  const digest = executions.map((e) => [
    e.id,
    e.status,
    readProjectRunStatus(e),
    e.loginSuccessAtMs ?? 0,
    e.runningPhaseStartedAtMs ?? 0,
    e.progressPercentage ?? 0,
    e.progressLogSeq ?? 0,
    e.progressLog?.length ?? 0,
    countProgressLogImages(e),
    e.startTime ?? 0,
    e.durationMs ?? 0,
    e.results?.length ?? 0,
    e.pfmHasCommittedSnapshot === true ? 1 : 0,
    e.pfmStepVersion ?? 0,
    e.pfmSnapshotPhase ?? "",
    e.chatDeliveryActive === true ? 1 : 0,
    e.lastErrorMessage ?? "",
    e.executorHint ?? "",
  ].join("|")).join("||");
  return `${selectedId ?? ""}##${digest}`;
}

function loadSelectedExecutionId(): string | null {
  try {
    const raw = localStorage.getItem(SELECTED_EXECUTION_KEY);
    const id = raw?.trim();
    return id || null;
  } catch {
    return null;
  }
}

function saveSelectedExecutionId(id: string | null): void {
  try {
    const normalized = id?.trim() || null;
    if (normalized) localStorage.setItem(SELECTED_EXECUTION_KEY, normalized);
    else localStorage.removeItem(SELECTED_EXECUTION_KEY);
  } catch {
    /* ignore */
  }
}

const TEMPLATE_ONLY_RUN_PREFIX = "__template__:";
const INTERACTIVE_SIDEBAR_CACHE_KEY = "ead-expui.interactive-sidebar.v1";

function loadInteractiveSidebarCache(): ProjectExecute[] {
  try {
    const raw = sessionStorage.getItem(INTERACTIVE_SIDEBAR_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ProjectExecute[];
    return Array.isArray(parsed) ? parsed.map((e) => normalizeExecutionFromApi(e)) : [];
  } catch {
    return [];
  }
}

function saveInteractiveSidebarCache(runs: ProjectExecute[]): void {
  try {
    sessionStorage.setItem(INTERACTIVE_SIDEBAR_CACHE_KEY, JSON.stringify(runs));
  } catch {
    /* ignore quota */
  }
}

export function isTemplateOnlySidebarRun(run: ProjectExecute): boolean {
  return String(run.id || "").startsWith(TEMPLATE_ONLY_RUN_PREFIX);
}

export type InteractiveTemplateSummary = { id: string; name: string };

function interactiveRunRank(run: ProjectExecute): [number, number, string] {
  const created = Number(
    (run as { createdAt?: number }).createdAt ??
      (run as { created_at?: number }).created_at ??
      run.startTime ??
      0,
  );
  return [Number(run.runNumber || 0), created, String(run.id || "")];
}

function compareInteractiveRunRank(a: ProjectExecute, b: ProjectExecute): number {
  const [aRun, aCreated, aId] = interactiveRunRank(a);
  const [bRun, bCreated, bId] = interactiveRunRank(b);
  if (bRun !== aRun) return bRun - aRun;
  if (bCreated !== aCreated) return bCreated - aCreated;
  return bId.localeCompare(aId);
}

/** Collapse duplicate rows that share the same execution id (list poll / cache races). */
export function dedupeExecutionsById(executions: ProjectExecute[]): ProjectExecute[] {
  const order: string[] = [];
  const byCanonical = new Map<string, ProjectExecute>();
  for (const ex of executions) {
    const id = String(ex.id || "").trim();
    if (!id) continue;
    const key = canonicalExecutionId(id);
    const prev = byCanonical.get(key);
    const merged = prev ? mergeExecution(prev, ex) : ex;
    merged.id = key;
    if (!prev) order.push(key);
    byCanonical.set(key, merged);
  }
  return order.map((id) => byCanonical.get(id)!);
}

function liveRunClusterKey(ex: ProjectExecute): string {
  const templateId = String(ex.linkedTemplateId || "").trim();
  const runNumber = Number(ex.runNumber || 0);
  if (templateId && Number.isFinite(runNumber) && runNumber > 0) {
    return `${templateId}::${runNumber}`;
  }
  return canonicalExecutionId(ex.id);
}

/**
 * Sidebar active runs: one row per live cluster, then gateway single-active-run policy.
 * Fixes duplicate "Run N, …" rows when stale copies share run_number/template but differ by id.
 */
export function pickCanonicalActiveRuns(live: ProjectExecute[]): ProjectExecute[] {
  const deduped = dedupeExecutionsById(live);
  if (deduped.length <= 1) return deduped;

  const clusters = new Map<string, ProjectExecute[]>();
  for (const ex of deduped) {
    const key = liveRunClusterKey(ex);
    const rows = clusters.get(key) || [];
    rows.push(ex);
    clusters.set(key, rows);
  }

  const canonical = [...clusters.values()].map(
    (rows) => [...rows].sort(compareInteractiveRunRank)[0],
  );
  if (canonical.length <= 1) return canonical;
  return [[...canonical].sort(compareInteractiveRunRank)[0]];
}

function normalizeExecutionsList(executions: ProjectExecute[]): ProjectExecute[] {
  return dedupeExecutionsById(executions).sort(sortByRunOrderDesc).slice(0, MAX_REMOTE_EXECUTIONS);
}

/** Active run first, else most recently finished (highest run_number / created_at). */
export function pickInteractiveRunForTemplate(runs: ProjectExecute[]): ProjectExecute | null {
  if (!runs.length) return null;
  const active = runs.filter((run) => isLiveProjectExecute(run));
  const pool = active.length ? active : runs.filter((run) => !isLiveProjectExecute(run));
  if (!pool.length) return null;
  return [...pool].sort(compareInteractiveRunRank)[0] ?? null;
}

export function createTemplateOnlySidebarRun(
  templateId: string,
  templateName: string,
): ProjectExecute {
  return {
    id: `${TEMPLATE_ONLY_RUN_PREFIX}${templateId}`,
    linkedTemplateId: templateId,
    name: templateName || "Unnamed template",
    status: "completed",
    chatDeliveryActive: false,
    projectRunStatus: ProjectRunStatus.Abandoned,
    startTime: 0,
    durationMs: 0,
    progressLog: [],
  } as ProjectExecute;
}

export function buildInteractiveSidebarFromTemplates(
  templates: InteractiveTemplateSummary[],
  executions: ProjectExecute[],
): ProjectExecute[] {
  const byTemplate = new Map<string, ProjectExecute[]>();
  for (const run of executions) {
    const templateId = String(run.linkedTemplateId || "").trim();
    if (!templateId || isTemplateOnlySidebarRun(run)) continue;
    const rows = byTemplate.get(templateId) || [];
    rows.push(run);
    byTemplate.set(templateId, rows);
  }

  const ordered = [...templates].sort((a, b) =>
    (a.name || a.id).localeCompare(b.name || b.id),
  );
  const out: ProjectExecute[] = [];
  for (const tmpl of ordered) {
    const templateId = String(tmpl.id || "").trim();
    if (!templateId) continue;
    const runs = byTemplate.get(templateId) || [];
    const picked = pickInteractiveRunForTemplate(runs);
    if (picked) {
      out.push(picked);
    } else {
      out.push(createTemplateOnlySidebarRun(templateId, tmpl.name || "Unnamed template"));
    }
  }
  return out;
}

export class ExecutionsStore extends EventTarget {
  private _executions: ProjectExecute[] = [];
  private _interactiveSidebarExecutions: ProjectExecute[] = [];
  private _selectedId: string | null = loadSelectedExecutionId();
  private _loading = false;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _activePageIndex = 0;
  private _finishedPageIndex = 0;
  private _subscriptions = 0;
  private _pollInFlight = false;
  private _detailPollInFlight = false;
  private _lastListPollAt = 0;
  private _executionsListEtag = "";
  private _lastSelectedDetailRefreshAt = 0;
  private _heavyPollingEnabled = false;
  private _sidebarPollTimer: ReturnType<typeof setInterval> | null = null;
  private _sidebarFetchInFlight = false;

  get loading(): boolean {
    return this._loading;
  }

  get executions(): ProjectExecute[] {
    return this._executions;
  }

  get selectedExecutionId(): string | null {
    return this._selectedId;
  }

  get selectedExecution(): ProjectExecute | undefined {
    return this._executionById(this._selectedId);
  }

  get activeRuns(): ProjectExecute[] {
    const live = this._executions.filter(
      (e) => {
        // Double-check terminal states to ensure "AI Finish" and other completed states are never active
        const status = String(e.status || "").toLowerCase();
        if (status === "completed" || status === "cancelled" || status === "failed" || status === "error") {
          return false;
        }
        return isLiveProjectExecute(e) && !isGhostExecutionId(e.id);
      },
    );
    return pickCanonicalActiveRuns(live);
  }

  get finishedRuns(): ProjectExecute[] {
    return this._executions.filter((e) => !isLiveProjectExecute(e));
  }

  /** One row per template (active run, else latest finished); from gateway index. */
  get interactiveSidebarRuns(): ProjectExecute[] {
    return this._interactiveSidebarExecutions;
  }

  get activePageCount(): number {
    return Math.max(1, Math.ceil(this.activeRuns.length / PAGE_SIZE));
  }

  get finishedPageCount(): number {
    return Math.max(1, Math.ceil(this.finishedRuns.length / PAGE_SIZE));
  }

  get activePageIndex(): number {
    return this._activePageIndex;
  }

  get finishedPageIndex(): number {
    return this._finishedPageIndex;
  }

  get pagedActiveRuns(): ProjectExecute[] {
    const start = this._activePageIndex * PAGE_SIZE;
    return this.activeRuns.slice(start, start + PAGE_SIZE);
  }

  get pagedFinishedRuns(): ProjectExecute[] {
    const start = this._finishedPageIndex * PAGE_SIZE;
    return this.finishedRuns.slice(start, start + PAGE_SIZE);
  }

  /**
   * When false (e.g. Project Plan tab), skip executions list/detail polling so the gateway
   * stays responsive for templates and /health.
   */
  setHeavyPollingEnabled(enabled: boolean): void {
    const next = Boolean(enabled);
    if (this._heavyPollingEnabled === next) return;
    this._heavyPollingEnabled = next;
    if (next) {
      void this.refresh();
    } else {
      this._stopExecutionPoll();
    }
    this._restartPollLoop();
    if (next) {
      void this.fetchInteractiveSidebar();
    }
  }

  isHeavyPollingEnabled(): boolean {
    return this._heavyPollingEnabled;
  }

  static shouldPollForTab(tab: string): boolean {
    return RUNS_TABS.has(tab);
  }

  subscribe(listener: () => void): () => void {
    const handler = () => listener();
    this.addEventListener("change", handler);
    this._subscriptions += 1;
    if (this._subscriptions === 1) {
      if (this._interactiveSidebarExecutions.length === 0) {
        const cached = loadInteractiveSidebarCache();
        if (cached.length) {
          this._interactiveSidebarExecutions = cached;
          this._emitChange();
        }
      }
      // Project Interactive is lightweight — load immediately (do not wait for executions.list).
      void this.fetchInteractiveSidebar();
      this._restartSidebarPoll();
      // Brief defer so /health and templates.list can win on a cold tab refresh.
      window.setTimeout(() => void this.start(), 400);
    }
    return () => {
      this.removeEventListener("change", handler);
      this._subscriptions = Math.max(0, this._subscriptions - 1);
      if (this._subscriptions === 0) {
        this.stop();
      }
    };
  }

  /** Refresh sidebar + executions after the gateway comes back online. */
  onGatewayReachable(): void {
    void this.fetchInteractiveSidebar();
    if (this._heavyPollingEnabled) {
      void this.refresh();
    } else {
      void this._fetchExecutions();
    }
  }

  async start(): Promise<void> {
    if (this._executions.length === 0) {
      this._executions = normalizeExecutionsList(
        loadExecutions()
          .map((e) => normalizeExecutionFromApi(e))
          .filter(includeInExecutionsList),
      );
      this._ensureSelection();
      this._emitChange();
    }
    // Always do one canonical gateway refresh on start so cached rows cannot
    // keep stale phase labels (e.g. Initializing after backend already Running).
    await this.refresh();
    void this.fetchInteractiveSidebar();
  }

  stop(): void {
    this._stopExecutionPoll();
    this._stopSidebarPoll();
  }

  private _stopExecutionPoll(): void {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _stopSidebarPoll(): void {
    if (this._sidebarPollTimer) {
      clearInterval(this._sidebarPollTimer);
      this._sidebarPollTimer = null;
    }
  }

  private _restartSidebarPoll(): void {
    if (this._sidebarPollTimer) {
      clearInterval(this._sidebarPollTimer);
      this._sidebarPollTimer = null;
    }
    if (this._subscriptions <= 0) return;
    this._sidebarPollTimer = setInterval(() => {
      void this.fetchInteractiveSidebar();
    }, 45_000);
  }

  async refresh(): Promise<void> {
    this._loading = true;
    this._emitChange();
    await this._fetchExecutions();
    this._loading = false;
    this._emitChange();
    this._restartPollLoop();
  }

  async refreshSelectionDetail(): Promise<void> {
    if (!this._selectedId) return;
    await this._refreshExecutionViaGet(this._selectedId, { skipBackfill: false });
    this._lastSelectedDetailRefreshAt = Date.now();
    this._emitChange();
  }

  /** Fast canonical phase refresh (skip heavy GET backfill). */
  syncSelectedExecutionPhase(): void {
    if (!this._selectedId) return;
    void this._refreshExecutionViaGet(this._selectedId, { skipBackfill: true }).then(() => {
      this._emitChange();
    });
  }

  setSelectedExecutionId(
    id: string | null,
    opts?: { skipDetailRefresh?: boolean },
  ): void {
    const normalized = id ? canonicalExecutionId(id) : null;
    if (this._selectedId === normalized) return;
    this._selectedId = normalized;
    saveSelectedExecutionId(this._selectedId);
    this._ensureSelection();
    this._ensurePageBounds();
    this._emitChange();
    this._restartPollLoop();
    if (normalized && !opts?.skipDetailRefresh) {
      const existing = this._executionById(normalized);
      const prs = existing != null ? readProjectRunStatus(existing) : null;
      const skipBackfill =
        prs === ProjectRunStatus.Initializing
        || (prs != null && isTerminalProjectRunStatus(prs));
      void this._refreshExecutionViaGet(normalized, { skipBackfill }).then(() => this._emitChange());
    }
  }

  /** Insert or update a run from POST /executions/run without waiting for a full list fetch. */
  upsertExecutionFromRunResponse(raw: unknown): ProjectExecute | null {
    const execution = unwrapExecutionFromRunResponse(raw);
    if (!execution?.id) return null;
    const idx = this._executions.findIndex((e) => e.id === execution.id);
    if (idx >= 0) {
      const next = [...this._executions];
      next[idx] = mergeExecution(next[idx], execution);
      this._executions = normalizeExecutionsList(next);
    } else {
      this._executions = normalizeExecutionsList([execution, ...this._executions]);
    }
    saveExecutions(this._executions);
    this._ensurePageBounds();
    this._emitChange();
    return execution;
  }

  setActivePage(index: number): void {
    this._activePageIndex = this._clampPage(index, this.activePageCount);
    this._emitChange();
  }

  setFinishedPage(index: number): void {
    this._finishedPageIndex = this._clampPage(index, this.finishedPageCount);
    this._emitChange();
  }

  async cancelExecution(id: string, mode: "finish" | "cancel" = "cancel"): Promise<void> {
    const result = await hermesClient.request<{ id?: string; status?: string }>("executions.cancel", {
      id,
      mode,
    });
    if (!result) {
      const detail = hermesClient.getLastRequestError();
      throw new Error(detail || "Could not stop the run. Check that the gateway is online.");
    }
    await this.refresh();
  }

  async extendExecutionBudget(id: string, extendMinutes: number): Promise<void> {
    const result = await hermesClient.request<{ id?: string; timeBudgetMinutes?: number }>(
      "executions.extendBudget",
      {
        id,
        extendMinutes,
      },
    );
    if (!result) {
      const detail = hermesClient.getLastRequestError();
      throw new Error(detail || "Could not extend run budget. Check that the gateway is online.");
    }
    await this.refresh();
  }

  async deleteExecution(id: string): Promise<void> {
    const result = await hermesClient.request<{ deleted?: boolean; executionId?: string }>(
      "executions.delete",
      { id, executionId: id },
    );
    if (!result?.deleted) {
      throw new Error("Hermes did not confirm the run was deleted.");
    }
    this._executions = this._executions.filter((e) => e.id !== id);
    if (this._selectedId === id) {
      this._selectedId = null;
      saveSelectedExecutionId(null);
    }
    saveExecutions(this._executions);
    this._ensureSelection();
    this._ensurePageBounds();
    this._emitChange();
    await this.refresh(true);
  }

  async invalidateExecution(id: string, reason: string): Promise<void> {
    await hermesClient.request("executions.invalidate", { id, executionId: id, reason });
    await this.refresh(true);
  }

  async setExecutionReportingActivity(id: string, active: boolean): Promise<void> {
    await hermesClient.request("executions.setReportingActivity", {
      id,
      executionId: id,
      active,
      status: active ? "active" : "closed",
    });
    await this.refresh(true);
  }

  async setExecutionContinuousLearning(
    id: string,
    enabled: boolean,
    reason?: string,
  ): Promise<void> {
    let updated = await hermesClient.request<ProjectExecute>("executions.setLearning", {
      id,
      executionId: id,
      validForContinuousLearning: enabled,
      reason,
    });

    if (!updated?.id && !enabled) {
      const lastErr = hermesClient.getLastRequestError() || "";
      if (lastErr.includes("404")) {
        await this.invalidateExecution(
          id,
          reason || "Excluded from interactive learning — run not worth keeping",
        );
        return;
      }
    }

    if (!updated?.id) {
      const lastErr = hermesClient.getLastRequestError() || "";
      if (lastErr.includes("404")) {
        throw new Error(
          "Gateway needs a restart to save learning settings (missing POST /v1/projects/executions/…/learning).",
        );
      }
      throw new Error(lastErr || "Could not update interactive learning for this run.");
    }

    this._executions = this._executions.map((run) => (run.id === id ? { ...run, ...updated } : run));
    saveExecutions(this._executions);
    this._emitChange();
    await this.refresh(true);
  }

  async setExecutionAnswerLanguage(id: string, aiAnswerLanguage: string): Promise<void> {
    const updated = await hermesClient.request<ProjectExecute>("executions.setAnswerLanguage", {
      id,
      executionId: id,
      aiAnswerLanguage,
    });
    if (!updated?.id) {
      const lastErr = hermesClient.getLastRequestError() || "";
      if (lastErr.includes("404")) {
        throw new Error(
          "Gateway needs restart to save AI Answer Language (missing /answer-language endpoint).",
        );
      }
      throw new Error(lastErr || "Could not update AI Answer Language for this run.");
    }
    this._executions = this._executions.map((run) => (run.id === id ? { ...run, ...updated } : run));
    saveExecutions(this._executions);
    this._emitChange();
    await this.refresh(true);
  }

  private _clampPage(next: number, pageCount: number): number {
    return Math.max(0, Math.min(next, Math.max(0, pageCount - 1)));
  }

  private _ensurePageBounds(): void {
    this._activePageIndex = this._clampPage(this._activePageIndex, this.activePageCount);
    this._finishedPageIndex = this._clampPage(this._finishedPageIndex, this.finishedPageCount);
  }

  private _executionById(id: string | null | undefined): ProjectExecute | undefined {
    const key = canonicalExecutionId(id);
    if (!key) return undefined;
    return this._executions.find((e) => canonicalExecutionId(e.id) === key);
  }

  private _ensureSelection(): void {
    if (this._selectedId && this._executionById(this._selectedId)) {
      this._selectedId = canonicalExecutionId(this._selectedId);
      saveSelectedExecutionId(this._selectedId);
      return;
    }
    const first = this.activeRuns[0] ?? this.finishedRuns[0] ?? null;
    this._selectedId = first?.id ?? null;
    saveSelectedExecutionId(this._selectedId);
  }

  private _preferFirstActiveSelection(): void {
    if (this._selectedId && this._executionById(this._selectedId)) return;
    const firstActive = this.activeRuns[0];
    if (!firstActive) return;
    this._selectedId = firstActive.id;
    saveSelectedExecutionId(this._selectedId);
    this._activePageIndex = 0;
  }

  private _emitChange(): void {
    this.dispatchEvent(new Event("change"));
  }

  private _restartPollLoop(): void {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
    const ms = this._nextPollIntervalMs();
    if (ms == null) return;
    this._pollTimer = setInterval(() => {
      void this._poll();
    }, ms);
    void this._pollSelectedDetailIfLive();
  }

  private async _pollSelectedDetailIfLive(): Promise<void> {
    if (!this._heavyPollingEnabled || this._detailPollInFlight) return;
    const selected = this.selectedExecution;
    if (!selected?.id || !isLiveProjectExecute(selected)) return;
    this._detailPollInFlight = true;
    try {
      const skipBackfill = readProjectRunStatus(selected) === ProjectRunStatus.Initializing;
      await this._refreshExecutionViaGet(selected.id, { skipBackfill });
      this._emitChange();
    } finally {
      this._detailPollInFlight = false;
    }
  }

  private _nextPollIntervalMs(): number | null {
    if (!this._heavyPollingEnabled) return null;
    const selected = this.selectedExecution;
    if (!selected) return null;
    if (isLiveProjectExecute(selected)) {
      if (readProjectRunStatus(selected) === ProjectRunStatus.Initializing) {
        return INITIALIZING_DETAIL_POLL_MS;
      }
      return LIVE_DETAIL_POLL_MS;
    }
    return IDLE_POLL_MS;
  }

  private async _poll(): Promise<void> {
    if (this._pollInFlight) return;
    if (!canPollGateway()) return;
    this._pollInFlight = true;
    try {
      await runGatewayPoll(async () => {
        const selected = this.selectedExecution;
        const live = selected && isLiveProjectExecute(selected);
        if (live && selected.id) {
          const skipBackfill = readProjectRunStatus(selected) === ProjectRunStatus.Initializing;
          await this._refreshExecutionViaGet(selected.id, { skipBackfill });
        }
        const now = Date.now();
        if (now - this._lastListPollAt >= ACTIVE_POLL_MS) {
          await this._fetchExecutions();
          this._lastListPollAt = now;
        }
      });
      this._emitChange();
      this._restartPollLoop();
    } finally {
      this._pollInFlight = false;
    }
  }

  /** Lightweight active-run probe for Start Run (avoids full executions.list). */
  async fetchActiveExecutionProbe(): Promise<ProjectExecute | null> {
    const result = await hermesClient.request<{
      execution?: Record<string, unknown>;
      executionId?: string;
    }>("executions.active");
    const raw = result?.execution;
    if (!raw || typeof raw !== "object") return null;
    const id = canonicalExecutionId(String(raw.id || result?.executionId || "").trim());
    if (!id) return null;
    const probePrs = pickPrsRaw(raw as ProjectExecute);
    const stub = normalizeExecutionFromApi({
      id,
      linkedTemplateId: String(raw.linkedTemplateId || raw.linked_template_id || ""),
      status: String(raw.status || "running"),
      name: "Active run",
      startTime: Number(raw.createdAt || raw.created_at || Date.now()),
      ...(probePrs !== undefined ? { projectRunStatus: probePrs } : {}),
    } as ProjectExecute);
    const idx = this._executions.findIndex((e) => canonicalExecutionId(e.id) === id);
    if (idx >= 0) {
      const existing = this._executions[idx];
      if (pickPrsRaw(existing) !== undefined && pickPrsRaw(stub) === undefined) {
        return existing;
      }
      const next = [...this._executions];
      next[idx] = mergeExecution(existing, stub);
      this._executions = normalizeExecutionsList(next);
    } else {
      this._executions = normalizeExecutionsList([stub, ...this._executions]);
    }
    this._emitChange();
    return this._executions.find((e) => e.id === id) ?? stub;
  }

  async fetchInteractiveSidebar(): Promise<void> {
    if (this._sidebarFetchInFlight) return;
    if (!canPollGateway()) return;
    this._sidebarFetchInFlight = true;
    try {
      await runGatewayPoll(() => this._fetchInteractiveSidebarInner());
    } finally {
      this._sidebarFetchInFlight = false;
    }
  }

  private async _fetchInteractiveSidebarInner(): Promise<void> {
    const result = await hermesClient.request<{ rows?: Array<Record<string, unknown>> }>(
      "templates.interactiveSidebar",
    );
    const apiRows = Array.isArray(result?.rows) ? result.rows : null;

    if (apiRows && apiRows.length > 0) {
      this._applyInteractiveSidebarRuns(this._buildInteractiveSidebarFromApiRows(apiRows));
      return;
    }

    const templates = await this._fetchTemplateSummariesForSidebar();
    if (templates.length > 0) {
      this._applyInteractiveSidebarRuns(
        buildInteractiveSidebarFromTemplates(templates, this._executions),
      );
      return;
    }

    if (this._interactiveSidebarExecutions.length === 0) {
      const cached = loadInteractiveSidebarCache();
      if (cached.length) {
        this._interactiveSidebarExecutions = cached;
        this._emitChange();
      }
    }
  }

  private async _fetchTemplateSummariesForSidebar(): Promise<InteractiveTemplateSummary[]> {
    const result = await hermesClient.request<{ templates?: Array<Record<string, unknown>> }>(
      "templates.list",
    );
    const rows = Array.isArray(result?.templates) ? result.templates : [];
    const out: InteractiveTemplateSummary[] = [];
    for (const row of rows) {
      const id = String(row?.id || "").trim();
      if (!id) continue;
      out.push({
        id,
        name: String(row?.name || "").trim() || "Unnamed template",
      });
    }
    return out;
  }

  private _buildInteractiveSidebarFromApiRows(
    rows: Array<Record<string, unknown>>,
  ): ProjectExecute[] {
    const existingById = new Map(this._executions.map((e) => [e.id, e]));
    const sidebarRuns: ProjectExecute[] = [];

    for (const row of rows) {
      const templateId = String(row.templateId || row.template_id || "").trim();
      const templateName = String(row.templateName || row.template_name || "").trim();
      const rawEx = row.execution;
      if (rawEx && typeof rawEx === "object") {
        const normalized = normalizeExecutionFromApi(rawEx as ProjectExecute);
        if (templateId && !String(normalized.linkedTemplateId || "").trim()) {
          normalized.linkedTemplateId = templateId;
        }
        const base = existingById.get(normalized.id);
        const merged = base ? mergeExecution(base, normalized) : normalized;
        sidebarRuns.push(merged);
        if (merged.id) {
          existingById.set(merged.id, merged);
          const idx = this._executions.findIndex((e) => e.id === merged.id);
          if (idx >= 0) {
            this._executions[idx] = merged;
          } else {
            this._executions.push(merged);
          }
        }
        continue;
      }
      if (!templateId) continue;
      sidebarRuns.push(
        createTemplateOnlySidebarRun(templateId, templateName || "Unnamed template"),
      );
    }

    return sidebarRuns;
  }

  private _applyInteractiveSidebarRuns(sidebarRuns: ProjectExecute[]): void {
    if (!sidebarRuns.length) return;
    this._interactiveSidebarExecutions = sidebarRuns;
    saveInteractiveSidebarCache(sidebarRuns);
    this._executions = normalizeExecutionsList(this._executions);
    saveExecutions(this._executions);
    this._emitChange();
  }

  private async _fetchExecutions(): Promise<void> {
    const result = await hermesClient.request<{ executions?: ProjectExecute[]; __etag?: string }>(
      "executions.list",
      {
        limit: MAX_REMOTE_EXECUTIONS,
        ifNoneMatch: this._executionsListEtag || undefined,
      },
    );

    if (isHermesNotModified(result)) {
      return;
    }

    if (result?.__etag) {
      this._executionsListEtag = result.__etag;
    }

    if (Array.isArray(result?.executions)) {
      const existingById = new Map(this._executions.map((e) => [e.id, e]));
      this._executions = normalizeExecutionsList(
        result.executions
          .map((e) => normalizeExecutionFromApi(e))
          .filter(includeInExecutionsList)
          .map((fresh) => {
            const base = existingById.get(fresh.id);
            return base ? mergeExecution(base, fresh) : fresh;
          }),
      );
      saveExecutions(this._executions);
    } else {
      // API is canonical: keep current in-memory rows if already loaded.
      // Only hydrate from local cache on cold start.
      if (this._executions.length === 0) {
        this._executions = normalizeExecutionsList(
          loadExecutions()
            .map((e) => normalizeExecutionFromApi(e))
            .filter(includeInExecutionsList),
        );
      }
    }

    this._ensureSelection();
    this._preferFirstActiveSelection();
    this._ensurePageBounds();
    void this.fetchInteractiveSidebar();
  }

  private async _refreshExecutionViaGet(
    id: string,
    opts?: { skipBackfill?: boolean },
  ): Promise<void> {
    const eid = id.trim();
    if (!eid) return;
    const raw = await hermesClient.request<unknown>("executions.get", {
      id: eid,
      executionId: eid,
      skipBackfill: opts?.skipBackfill === true,
    });
    const fresh = unwrapExecutionFromGet(raw);
    if (!fresh) return;
    const idx = this._executions.findIndex((e) => e.id === eid);
    if (idx >= 0) {
      const next = [...this._executions];
      next[idx] = mergeExecution(next[idx], fresh);
      this._executions = normalizeExecutionsList(next);
    } else {
      this._executions = normalizeExecutionsList([syncCanonicalRunFields(fresh), ...this._executions]);
    }
    saveExecutions(this._executions);
  }
}

export const executionsStore = new ExecutionsStore();
